import React, { MutableRefObject, useEffect, useState } from 'react';
import { Store } from '../../store';
import * as EmailValidator from 'email-validator';
import { Box, Button, Input, Text, Tooltip } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { getIntroField } from '../../utils';
import { useDimensions } from '../../utils/dimensions';
import posthog from 'posthog-js';

const MAX_LENGTH = 180;
const LENGTH_BOUNDARY = MAX_LENGTH * (2 / 3);

export const EmailCollectionInput: React.FC<{
  submitEmail: (email: string) => void;
  skipEmail?: () => void;
  emailSkipped?: boolean;
  submitLabel: string;
  isIntroSequence: boolean;
  emailSubmission?: { censored?: string; completed?: boolean };
  containerRef: MutableRefObject<HTMLDivElement | null>;
}> = (props) => {
  const {
    submitEmail,
    emailSkipped,
    submitLabel,
    isIntroSequence,
    emailSubmission,
    containerRef,
    skipEmail
  } = props;
  const [email, setEmail] = useState('');
  const [invalidEmail, setInvalidEmail] = useState(false);
  const labels = Store.useState((s) => s.labels);
  const skippedEmail = Store.useState((s) => s.skippedEmail) || emailSkipped;

  const control = useAnimation();
  const minimizeEmailField = () => {
    control.start({
      opacity: 0,
      marginTop: 0,
      transition: { duration: 0.2, delay: 0, ease: 'easeInOut' }
    });
    control.start({
      height: 0,
      transition: { duration: 0.2, delay: 0.3, ease: 'easeInOut' }
    });
    control.start({
      display: 'none',
      transition: { delay: 0.5, duration: 0.2, ease: 'easeInOut' }
    });
  };
  const trySubmitEmail = () => {
    if (email && EmailValidator.validate(email)) {
      posthog.capture('SEND-EMAIL');
      submitEmail(email);
      minimizeEmailField();
    } else {
      setInvalidEmail(true);
    }
  };
  useEffect(() => {
    if (skippedEmail) {
      minimizeEmailField();
    } else {
      control.start({
        display: 'block',
        height: '100%',
        opacity: 1,
        transition: { delay: 0.5, duration: 0.2, ease: 'easeInOut' }
      });
    }
  }, [skippedEmail]);

  const hideSubmission = isIntroSequence ? getIntroField('introMessageEmail') : emailSubmission?.completed;
  const { isDesktop } = useDimensions();
  if (hideSubmission) return <></>;
  return (
    <motion.div animate={control} key={'PII-Collection'} style={{ marginTop: 12 }}>
      <Box>
        <Tooltip
          isOpen={invalidEmail}
          label={labels.invalidEmail}
          placement="auto"
          aria-label="invalid email"
          hasArrow
          variant="invalid-input"
          portalProps={{
            containerRef
          }}
        >
          <Input
            variant={'in-chat-input'}
            isInvalid={invalidEmail}
            autoFocus={!skippedEmail && isDesktop}
            aria-labelledby="remark-email-collection"
            inputMode="email"
            enterKeyHint="send"
            maxLength={MAX_LENGTH}
            placeholder={labels.emailPlaceholder}
            onChange={(e) => {
              setEmail(e.target.value);
              if (invalidEmail && EmailValidator.validate(e.target.value)) {
                setInvalidEmail(false);
              }
            }}
            value={email}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                trySubmitEmail();
              }
            }}
          />
        </Tooltip>
        <Text
          variant="paragraph-small"
          color="scheme.contentTertiary"
          textAlign="end"
          lineHeight="12px"
          fontSize="10px"
          marginBottom="0"
          as="label"
          display="block"
          float="right"
          hidden={email.length <= LENGTH_BOUNDARY}
        >
          {email.length} / {MAX_LENGTH}
        </Text>

        <Button
          variant="submit-cta"
          mt={email.length <= LENGTH_BOUNDARY ? '12px' : 0}
          disabled={!email.length || invalidEmail}
          onClick={() => trySubmitEmail()}
        >
          {submitLabel}
        </Button>
        {skipEmail && (
          <Text
            _hover={{ textDecoration: 'underline' }}
            cursor="pointer"
            variant="label-small"
            color="scheme.contentTertiary"
            onClick={() => {
              skipEmail();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                skipEmail();
              }
            }}
            textAlign="center"
            mt="12px"
            tabIndex={0}
          >
            {labels.emailSubmissionIntroSkip}
          </Text>
        )}
      </Box>
    </motion.div>
  );
};
